import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Download, X, Smartphone } from 'lucide-react';

export default function InstallBanner() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [deviceType, setDeviceType] = useState(null);

  useEffect(() => {
    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    // Check if banner was dismissed
    const dismissed = localStorage.getItem('installBannerDismissed');
    
    // Detect mobile device
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /iphone|ipad|ipod|android/.test(userAgent);
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    
    if (isMobile && !isStandalone && !dismissed) {
      setDeviceType(isIOS ? 'ios' : isAndroid ? 'android' : null);
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 2000);
    }
  }, []);

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('installBannerDismissed', 'true');
  };

  const handleInstall = () => {
    navigate('/install');
  };

  const texts = {
    it: {
      title: 'Installa Smart Data Collection',
      description: 'Accesso rapido dalla tua Home',
      install: 'Installa',
      later: 'Dopo'
    },
    en: {
      title: 'Install Smart Data Collection',
      description: 'Quick access from your Home',
      install: 'Install',
      later: 'Later'
    }
  };

  const txt = texts[language] || texts.it;

  if (!showBanner) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 animate-slide-down"
      style={{
        animation: 'slideDown 0.3s ease-out forwards'
      }}
    >
      <div className="mx-4 mt-4 rounded-lg overflow-hidden shadow-xl shadow-neon-green/20">
        {/* Glass morphism background */}
        <div 
          className="backdrop-blur-md bg-cyber-gray/90 border border-neon-green/30 p-4"
          style={{
            background: 'linear-gradient(135deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)'
          }}
        >
          <div className="flex items-center gap-4">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-lg bg-neon-green/20 flex items-center justify-center animate-bounce-subtle">
                <Smartphone className="w-6 h-6 text-neon-green" />
              </div>
            </div>
            
            {/* Text */}
            <div className="flex-1 min-w-0">
              <h3 className="font-orbitron text-white text-sm font-bold truncate">
                {txt.title}
              </h3>
              <p className="text-xs text-gray-400 font-mono">
                {txt.description}
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex-shrink-0 flex items-center gap-2">
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-xs font-mono text-gray-400 hover:text-white transition-colors"
              >
                {txt.later}
              </button>
              <button
                onClick={handleInstall}
                className="px-4 py-1.5 text-xs font-mono bg-neon-green text-cyber-dark font-bold rounded hover:bg-neon-green/90 transition-colors flex items-center gap-1"
              >
                <Download className="w-3 h-3" />
                {txt.install}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }
        
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
